import { useTranslation } from "react-i18next";
import { Manager } from "../../../Manager";
import { fetchProducts, type Product } from "../../../../state/products";
import {
  searchSuppliers,
  fetchSupplierById,
  type Supplier,
} from "../../../../state/suppliers";
import {
  searchBrands,
  fetchBrandById,
  type Brand,
} from "../../../../state/brands";
import { supabase } from "../../../../lib/supabase";
import type { SearchParams, SortParams } from "../../../Manager/types";
import { useCallback } from "react";
import { Box } from "@radix-ui/themes";

const SEARCH_COLUMNS = ["name", "code"];

export function ManageProducts() {
  const { t } = useTranslation();

  const fetchData = useCallback(
    (
      limit?: number,
      offset?: number,
      searchParams?: SearchParams,
      sortParams?: SortParams,
    ) => fetchProducts(limit, offset, searchParams, sortParams),
    [],
  );

  const handleSubmit = async (
    data: Record<string, unknown>,
    item: Product | null,
  ) => {
    if (!item) {
      throw new Error("Cannot create products from UI");
    }

    // Only update the name (as per PRD)
    const productData = {
      name: data.name as string,
    };

    const { error } = await supabase
      .from("products")
      .update(productData)
      .eq("id", item.id);
    if (error) throw new Error(error.message);
  };

  return (
    <Box p="4">
      <Manager.Root<Product> title={t("products.title")} fetchData={fetchData}>
        <Manager.Search>
          <Manager.TextSearch
            columns={SEARCH_COLUMNS}
            placeholder={t("products.searchPlaceholder")}
          />
          <Manager.AutocompleteSearch<Supplier>
            filterKey="supplier_id"
            label={t("products.supplier")}
            placeholder={t("suppliers.searchPlaceholder")}
            searchFn={searchSuppliers}
            fetchByIdFn={fetchSupplierById}
            getOptionLabel={(s) => s.name}
            getOptionValue={(s) => s.id}
          />
          <Manager.AutocompleteSearch<Brand>
            filterKey="brand_id"
            label={t("products.brand")}
            placeholder={t("brands.searchPlaceholder")}
            searchFn={searchBrands}
            fetchByIdFn={fetchBrandById}
            getOptionLabel={(b) => b.name}
            getOptionValue={(b) => b.id}
          />
        </Manager.Search>

        <Manager.Sort
          columns={[{ key: "name", isDefault: true }, { key: "code" }]}
        />

        <Manager.Table>
          <Manager.Column<Product>
            header={t("products.name")}
            accessor={(p) => p.name}
            sortKey="name"
          />
          <Manager.Column<Product>
            header={t("products.code")}
            accessor={(p) => p.code ?? "—"}
            sortKey="code"
          />
          <Manager.Column<Product>
            header={t("products.brand")}
            accessor={(p) => p.brands?.name ?? "—"}
          />
          <Manager.Column<Product>
            header={t("products.supplier")}
            accessor={(p) => p.brands?.suppliers?.name ?? "—"}
          />
          <Manager.Actions>
            <Manager.EditAction />
          </Manager.Actions>
        </Manager.Table>

        <Manager.FormDialog<Product>
          title={() => t("products.editProduct")}
          description={() => t("products.editDescription")}
          onSubmit={handleSubmit}
          validate={(data) => (data.name as string)?.trim() !== ""}
        >
          <Manager.TextField
            name="name"
            label={t("products.name")}
            required
            placeholder={t("products.namePlaceholder")}
            getValue={(item) => (item as Product).name}
          />
        </Manager.FormDialog>
      </Manager.Root>
    </Box>
  );
}
